import Dexie, { type EntityTable } from 'dexie';
import type { ParteDelDiaPorCamada, ParteDelDiaUpsertInput } from '@/lib/types';
import type { OfflineBundle, OfflineOperation } from './domainTypes';

export interface Snapshot {
  id: string;
  ownerId: number;
  key: string;
  data: unknown;
  savedAt: number;
  generation?: string;
}
export interface PendingParte {
  id: string;
  ownerId: number;
  explotacionId: number;
  camadaId: number;
  fecha: string;
  dayKey: string;
  body: ParteDelDiaUpsertInput;
  payload: ParteDelDiaUpsertInput & {
    operation_uuid: string;
    expected_revision: string;
    expected_explotacion_id: number;
  };
  baseline: ParteDelDiaPorCamada;
  parentId: string | null;
  createdAt: number;
  status: 'pending' | 'sending' | 'retry' | 'auth' | 'conflict' | 'error';
  message?: string;
  actual?: ParteDelDiaPorCamada;
  attempts: number;
}
export interface Lease {
  id: string;
  holder: string;
  expiresAt: number;
}
export interface BundleRecord {
  id: string;
  ownerId: number;
  generation: string;
  data: OfflineBundle;
}
export interface IdMapping {
  id: string;
  ownerId: number;
  explotacionId: number;
  entity: string;
  localId: number;
  remoteId: number;
  operationId: string;
  guardedRevision?: string;
  guardedContexts?: Record<string, string>;
}
export interface OperationPayload {
  operation_uuid: string;
  expected_explotacion_id: number;
  expected_revision: string | null;
  method: string;
  path: string;
  body: unknown;
  approved_snapshot?: unknown;
  context_revisions?: Record<string, string>;
}
export interface PendingOperation extends OfflineOperation {
  id: string;
  ownerId: number;
  explotacionId: number;
  scope: string;
  expectedRevision: string | null;
  dependencies: string[];
  parentId: string | null;
  timestamp: number;
  status: PendingParte['status'];
  attempts: number;
  payload?: OperationPayload;
  message?: string;
  actual?: OfflineBundle;
  approvedSnapshot?: unknown;
  entity?: string;
  label?: string;
  legacy?: boolean;
  legacyActual?: ParteDelDiaPorCamada;
  affectedScopes?: string[];
  contextRevisions?: Record<string, string>;
  versionParents?: Record<string, string>;
  createdIds?: Array<{ entity: string; localId: number; naturalKey?: Record<string, unknown> }>;
}

/** Keep the legacy idempotency body intact, including already attempted UUIDs. */
export function migrateParte(row: PendingParte): PendingOperation {
  const path = `/v1/camadas/${row.camadaId}/registros-diarios`;
  return {
    id: row.id,
    ownerId: row.ownerId,
    explotacionId: row.explotacionId,
    method: 'POST',
    path,
    body: row.payload,
    scope: `camada:${row.camadaId}`,
    expectedRevision: null,
    dependencies: row.parentId ? [row.parentId] : [],
    parentId: row.parentId,
    timestamp: row.createdAt,
    createdAt: new Date(row.createdAt).toISOString(),
    status: row.status,
    attempts: row.attempts,
    message: row.message,
    legacy: true,
    legacyActual: row.actual,
    payload: {
      operation_uuid: row.id,
      expected_explotacion_id: row.explotacionId,
      expected_revision: null,
      method: 'POST',
      path,
      body: row.payload,
    },
  };
}

export const offlineDb = new Dexie('camadero-offline-parts-v1') as Dexie & {
  snapshots: EntityTable<Snapshot, 'id'>;
  operations: EntityTable<PendingParte, 'id'>;
  leases: EntityTable<Lease, 'id'>;
  bundles: EntityTable<BundleRecord, 'id'>;
  mutations: EntityTable<PendingOperation, 'id'>;
  mappings: EntityTable<IdMapping, 'id'>;
};
offlineDb
  .version(1)
  .stores({ snapshots: 'id, ownerId', operations: 'id, ownerId, dayKey, createdAt', leases: 'id' });
offlineDb
  .version(2)
  .stores({
    snapshots: 'id, ownerId',
    operations: 'id, ownerId, dayKey, createdAt',
    leases: 'id',
    bundles: 'id, ownerId',
    mutations: 'id, ownerId, scope, timestamp',
    mappings: 'id, ownerId',
  })
  .upgrade(async (transaction) => {
    const rows = (await transaction.table('operations').toArray()) as PendingParte[];
    for (const row of rows) await transaction.table('mutations').put(migrateParte(row));
    await transaction.table('operations').clear();
  });
